import { IRequestOptions, IResponseResult } from 'models/api.model'
import { IPitPatCouponResponseRaw } from 'models/pitPat.model'
import { IWoofyPrepareConversationResponseRaw } from 'models/woofy.model'
import { RequestMethod } from 'root-constants'
import { ApiService } from 'services/api.service'

export class AdminApi {
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  getBreeds(locale: string): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
      params: { sort_by: 'name', locale },
    }

    return this.api.makeRequest<IResponseResult<Response>>(
      'api/v1/breeds',
      options,
    )
  }

  getCommands(locale: string): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { listAll: true },
      body: { locale },
    }

    return this.api.makeRequest<IResponseResult<Response>>(
      'api/v1/tricks',
      options,
    )
  }

  createUserProfile({
    id,
    timestamp,
    data,
    token,
  }: {
    id: string
    timestamp: number
    data: any
    token: string
  }): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
      body: [{ id, timestamp, ...data }],
    }

    return this.api.makeRequest<IResponseResult<Response>>(
      'api/v1/dog/profiles',
      options,
    )
  }

  sendUserInfoToApp({
    uuid,
    token,
  }: {
    uuid: string
    token: string
  }): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
      body: {
        uuid,
      },
    }

    return this.api.makeRequest<IResponseResult<Response>>(
      'api/v1/users/meta',
      options,
    )
  }

  getUserProfileInfo(token: string): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    }

    return this.api.makeRequest<Response>('api/v1/dog/profile', options)
  }

  getDogsProfile(token: string): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    }

    return this.api.makeRequest<Response>('api/v1/dog/profiles', options)
  }

  sendDogTrainerInfo({
    token,
    email,
    trainerEmail,
    userName,
  }: {
    token: string
    email: string
    trainerEmail: string
    userName: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        trainer_email: trainerEmail,
        user_name: userName,
        user_email: email,
      },
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    }

    return this.api.makeRequest('api/v1/dogtrainer/chat', options)
  }

  confirmPurchase(token: string): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
      body: {},
    }

    return this.api.makeRequest('api/v1/dogtrainer/chat/purchased', options)
  }

  getUserHasChat(token: string): Promise<IResponseResult<Response>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    }

    return this.api.makeRequest<Response>('api/v1/dogtrainer/status', options)
  }

  prepareConversation({
    id,
    conversationId,
    version,
  }: {
    id: string
    conversationId: string
    version: 1 | 2
  }): Promise<IResponseResult<IWoofyPrepareConversationResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        id,
        conversation_id: conversationId,
        version,
      },
    }

    return this.api.makeRequest<IWoofyPrepareConversationResponseRaw>(
      'api/v1/conversation/prepare',
      options,
    )
  }

  storeConversation({
    id,
    conversationId,
    token,
  }: {
    id: string
    conversationId: string
    token: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
      body: {
        id,
        conversation_id: conversationId,
      },
    }

    return this.api.makeRequest<never>('api/v1/conversation/store', options)
  }

  getCoupon({
    uuid,
  }: {
    uuid: string
  }): Promise<IResponseResult<IPitPatCouponResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
      params: {
        product_type: 'activity',
        uuid,
      },
    }

    return this.api.makeRequest<IPitPatCouponResponseRaw>(
      'api/v1/pitpat/promo/code',
      options,
    )
  }
}
